import type { AutomationType } from '@playbook-technologies/common-enums';

export type AutomationConfigField =
  | 'firstWorkout'
  | 'otpPitchTargetProductId'
  | 'includePushes';

type AutomationCopy = {
  label: string;
  description: string;
  disableConfirmation: { title: string; body: string };
  /** Send-test copy variants; empty when the sequence has a single variant. */
  variants: readonly string[];
  /** Mirrors creator-api `allowedConfigKeys`: which jsonb config keys the
   *  type reads. Anything else is rejected by the server with a 400. */
  configFields: readonly AutomationConfigField[];
  /** The server gates enabling on data it owns (prices, price paths), so
   *  the row shows a readiness line instead of a config field. */
  serverReadinessCheck: boolean;
};

const disableBody = (subject: string) =>
  `Active ${subject} sequences will be stopped and cannot be resumed. Turning this automation back on will apply only to new enrollments.`;

export const AUTOMATION_COPY = {
  abandoned_cart: {
    label: 'Abandoned cart emails',
    description:
      'Automatically follow up with customers who leave checkout without completing a purchase.',
    disableConfirmation: {
      title: 'Turn off abandoned cart emails?',
      body: 'Active abandoned-cart email sequences will be stopped and cannot be resumed. Turning this automation back on will apply only to new abandoned carts.',
    },
    variants: ['subscription', 'otp', 'offer'],
    configFields: [],
    serverReadinessCheck: false,
  },
  trial_conversion: {
    label: 'Trial conversion',
    description:
      'Nudge trialists towards their first workout and a paid subscription before the trial ends.',
    disableConfirmation: {
      title: 'Turn off trial conversion?',
      body: disableBody('trial-conversion'),
    },
    variants: [],
    configFields: ['firstWorkout', 'includePushes'],
    serverReadinessCheck: false,
  },
  otp_to_subscription: {
    label: 'One-time purchase to subscription',
    description: 'Pitch a subscription to customers after a one-time purchase.',
    disableConfirmation: {
      title: 'Turn off one-time purchase to subscription?',
      body: disableBody('OTP-to-subscription'),
    },
    variants: [],
    configFields: ['otpPitchTargetProductId', 'includePushes'],
    serverReadinessCheck: false,
  },
  monthly_to_annual: {
    label: 'Monthly to annual',
    description: 'Offer monthly subscribers a switch to the annual plan.',
    disableConfirmation: {
      title: 'Turn off monthly to annual?',
      body: disableBody('monthly-to-annual'),
    },
    variants: [],
    configFields: ['includePushes'],
    serverReadinessCheck: true,
  },
  workout_congrats: {
    label: 'Workout congratulations',
    description: 'Congratulate members after they complete a workout.',
    disableConfirmation: {
      title: 'Turn off workout congratulations?',
      body: disableBody('workout-congratulations'),
    },
    variants: [],
    configFields: ['includePushes'],
    serverReadinessCheck: false,
  },
} satisfies Record<AutomationType, AutomationCopy>;

export const AUTOMATION_ORDER = Object.keys(
  AUTOMATION_COPY,
) as readonly AutomationType[];

export const isKnownAutomationType = (value: string): value is AutomationType =>
  Object.prototype.hasOwnProperty.call(AUTOMATION_COPY, value);
